import { useMemo, useState } from "react";
import { useMutation, useQuery } from "@tanstack/react-query";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import { toast } from "react-toastify";
import { MdBusiness, MdEmail, MdSecurity } from "react-icons/md";

import { PublicInvitesApi } from "../../api/common/apiIndex";
import CountrySelect from "../common/commonUtils/CountrySelect";
import PhoneNumberInput from "../common/userRegistration/PhoneNumberInput";
import PasswordField from "../common/userRegistration/PasswordField.js";
import PasswordStrength from "../common/userRegistration/PasswordStrength.js";
import { isValidPassword, passwordStrength } from "../../utils/validators";

const INITIAL_FORM = {
  firstName: "",
  lastName: "",
  country: "",
  phone: "",
  phoneValid: false,
  password: "",
  confirmPassword: "",
};

const buildErrorMessage = (error, fallback = "Something went wrong.") =>
  error?.response?.data?.message ||
  error?.response?.data ||
  error?.message ||
  fallback;

export default function CompleteInvite() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token") || "";

  const [form, setForm] = useState(INITIAL_FORM);
  const [touched, setTouched] = useState({});
  const [showErrors, setShowErrors] = useState(false);

  const { data, isLoading, isError, error, refetch } = useQuery({
    queryKey: ["invite-info", token],
    queryFn: () => PublicInvitesApi.validate(token),
    enabled: !!token,
  });

  const completeInvite = useMutation({
    mutationFn: (payload) => PublicInvitesApi.complete(payload),
    onSuccess: () => {
      toast.success("Registration complete! You can now log in.");
      navigate("/login", { replace: true });
    },
    onError: (err) => {
      toast.error(buildErrorMessage(err, "Unable to complete invite."));
    },
  });

  const updateForm = (patch) => setForm((prev) => ({ ...prev, ...patch }));
  const markTouched = (field) => setTouched((prev) => ({ ...prev, [field]: true }));

  const trimmedFirst = form.firstName.trim();
  const trimmedLast = form.lastName.trim();
  const hasErrors = useMemo(() => {
    const base = {
      firstName: !trimmedFirst,
      lastName: !trimmedLast,
      country: !form.country,
      phone: !form.phone || !form.phoneValid,
      password: !isValidPassword(form.password),
      confirm: !form.confirmPassword || form.password !== form.confirmPassword,
    };
    return {
      ...base,
      any: Object.values(base).some(Boolean),
    };
  }, [trimmedFirst, trimmedLast, form.country, form.phone, form.phoneValid, form.password, form.confirmPassword]);

  const strength = passwordStrength(form.password);

  const handleSubmit = (event) => {
    event.preventDefault();
    setShowErrors(true);
    if (hasErrors.any || completeInvite.isPending) return;
    completeInvite.mutate({
      token,
      firstName: trimmedFirst,
      lastName: trimmedLast,
      country: form.country,
      phone: form.phone,
      password: form.password,
    });
  };

  if (!token) {
    return (
      <div className="container py-5">
        <div className="mx-auto" style={{ maxWidth: 520 }}>
          <div className="card border-0 shadow-sm rounded-4">
            <div className="card-body p-4 text-center">
              <h1 className="h5">Missing invitation token</h1>
              <p className="text-muted">
                Use the invite link from your email. If the link expired, ask your admin to send a new invite.
              </p>
              <Link to="/login" className="btn btn-outline-primary rounded-pill">
                Go to login
              </Link>
            </div>
          </div>
        </div>
      </div>
    );
  }

  const renderStateCard = (content) => (
    <div className="mx-auto" style={{ maxWidth: 720 }}>
      <div className="card border-0 shadow-sm rounded-4">
        <div className="card-body p-4">{content}</div>
      </div>
    </div>
  );

  if (isLoading) {
    return (
      <div className="container py-5 d-flex justify-content-center">
        {renderStateCard(
          <div className="text-center">
            <div className="spinner-border text-primary" role="status" aria-hidden="true" />
            <p className="mt-3 mb-0">Validating your invitation…</p>
          </div>
        )}
      </div>
    );
  }

  if (isError) {
    return (
      <div className="container py-5 d-flex justify-content-center">
        {renderStateCard(
          <>
            <h1 className="h5">We couldn't validate that link</h1>
            <p className="text-muted">
              {buildErrorMessage(error, "The invitation may be invalid or expired.")}
            </p>
            <div className="d-flex flex-wrap gap-2">
              <button type="button" className="btn btn-outline-secondary" onClick={() => refetch()}>
                Try again
              </button>
              <Link to="/login" className="btn btn-primary">
                Go to login
              </Link>
            </div>
          </>
        )}
      </div>
    );
  }

  const inviteInfo = data || {};
  const invitedRoles = Array.isArray(inviteInfo.roles) ? inviteInfo.roles : [];
  const formatRole = (role) =>
    String(role || "")
      .toLowerCase()
      .replace(/_/g, " ")
      .replace(/^\w/, (c) => c.toUpperCase());
  const phoneInputKey = form.country || "default";
  const showFirstErr = (showErrors || touched.firstName) && hasErrors.firstName;
  const showLastErr = (showErrors || touched.lastName) && hasErrors.lastName;
  const showCountryErr = (showErrors || touched.country) && hasErrors.country;
  const showPhoneErr = (showErrors || touched.phone) && hasErrors.phone;
  const showPasswordErr = (showErrors || touched.password) && hasErrors.password;
  const showConfirmErr = (showErrors || touched.confirmPassword) && hasErrors.confirm;

  return (
    <div className="container py-5">
      <div className="mx-auto" style={{ maxWidth: 760 }}>
        <div className="card border-0 shadow-sm rounded-4 mb-3">
          <div className="card-body p-4">
            <div className="d-flex flex-wrap align-items-center gap-3">
              <span className="badge rounded-pill bg-primary-subtle text-primary-emphasis d-inline-flex align-items-center gap-2">
                <MdBusiness /> Invitation for {inviteInfo.businessName || "your business"}
              </span>
              <div className="ms-auto text-muted small d-flex align-items-center gap-2">
                <MdEmail aria-hidden="true" />
                {inviteInfo.email}
              </div>
            </div>
            <p className="mt-3 mb-0">
              Finish creating your user account for{" "}
              <strong>{inviteInfo.businessName || "your business"}</strong>. The email above will be your username.
            </p>
            {invitedRoles.length > 0 && (
              <div className="mt-3 d-inline-flex align-items-center gap-2 text-muted small">
                <MdSecurity aria-hidden="true" />
                Roles: {invitedRoles.map(formatRole).join(", ")}
              </div>
            )}
          </div>
        </div>

        <div className="card border-0 shadow-sm rounded-4">
          <div className="card-body p-4">
            <form onSubmit={handleSubmit} noValidate>
              <div className="row g-3">
                <div className="col-12 col-md-6">
                  <label className="form-label">First name</label>
                  <input
                    type="text"
                    className={`form-control ${showFirstErr ? "is-invalid" : ""}`}
                    value={form.firstName}
                    onChange={(event) => updateForm({ firstName: event.target.value })}
                    onBlur={() => markTouched("firstName")}
                    placeholder="Amina"
                    autoComplete="given-name"
                  />
                  {showFirstErr && <div className="invalid-feedback">First name is required.</div>}
                </div>
                <div className="col-12 col-md-6">
                  <label className="form-label">Last name</label>
                  <input
                    type="text"
                    className={`form-control ${showLastErr ? "is-invalid" : ""}`}
                    value={form.lastName}
                    onChange={(event) => updateForm({ lastName: event.target.value })}
                    onBlur={() => markTouched("lastName")}
                    placeholder="Diallo"
                    autoComplete="family-name"
                  />
                  {showLastErr && <div className="invalid-feedback">Last name is required.</div>}
                </div>

                <div className="col-12 col-md-6">
                  <label className="form-label">Country</label>
                  <CountrySelect
                    value={form.country}
                    onChange={(code) => {
                      updateForm({
                        country: (code || "").toUpperCase(),
                        phone: "",
                        phoneValid: false,
                      });
                    }}
                    showFlagInValue
                  />
                  {showCountryErr && <div className="invalid-feedback d-block">Country is required.</div>}
                </div>

                <div className="col-12 col-md-6">
                  <label className="form-label">Phone number</label>
                  <div className={`form-control p-0 ${showPhoneErr ? "is-invalid" : ""}`}>
                    <PhoneNumberInput
                      key={phoneInputKey}
                      countryName={form.country}
                      value={form.phone}
                      onChange={(data) =>
                        updateForm({
                          phone: data.phone ? "+" + data.phone : "",
                          phoneValid: data.isValid,
                        })
                      }
                      disabled={!form.country}
                      inputClass="w-100 border-0 shadow-none bg-body"
                      buttonClass="btn bg-body border-0"
                    />
                  </div>
                  {showPhoneErr && (
                    <div className="text-danger small mt-1">Enter a valid phone number with country code.</div>
                  )}
                  {!form.country && (
                    <div className="text-muted small mt-1">Select a country to enable phone input.</div>
                  )}
                </div>
              </div>

              <hr className="my-4" />

              <PasswordField
                label="Password"
                name="password"
                value={form.password}
                onChange={(event) => updateForm({ password: event.target.value })}
                onBlur={() => markTouched("password")}
                placeholder="Create a password"
                invalid={showPasswordErr}
                invalidText="Must include upper, lower, digit, symbol (8–128 chars)."
              />

              <PasswordField
                label="Confirm password"
                name="confirmPassword"
                value={form.confirmPassword}
                onChange={(event) => updateForm({ confirmPassword: event.target.value })}
                onBlur={() => markTouched("confirmPassword")}
                placeholder="Repeat password"
                invalid={showConfirmErr}
                invalidText="Passwords must match."
              />

              {form.password && <PasswordStrength strength={strength} />}

              <div className="d-flex justify-content-between align-items-center mt-4 flex-wrap gap-2">
                <Link to="/login" className="btn btn-link text-decoration-none px-0">
                  Back to login
                </Link>
                <button
                  type="submit"
                  className="btn btn-primary rounded-pill d-inline-flex align-items-center gap-2"
                  disabled={completeInvite.isPending}
                >
                  {completeInvite.isPending && (
                    <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true" />
                  )}
                  Complete registration
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
}
